import { mapScopeTypeToBackendValue } from './utils'

const LIST_MEETINGS_SCOPE_KIND_BY_BACKEND_VALUE = {
  my_calendly: 'MY_CALENDLY',
  user_single: 'USER_SINGLE',
  user_group: 'USER_GROUP',
  page_single: 'PAGE_SINGLE',
  page: 'PAGE',
  all_users: 'ALL_USERS',
  all_users_and_teams: 'ALL_USERS_AND_TEAMS',
} as const

const LIST_MEETINGS_SCOPES_REQUIRING_ID = new Set([
  'USER_SINGLE',
  'USER_GROUP',
  'PAGE_SINGLE',
])

export type ListMeetingsScopeInput = {
  kind: string
  id?: string
  userUuids?: string[]
}

/** Route or backend scope slugs plus optional filter-modal user ids. */
export type ListMeetingsScopeUiFilters = {
  scopeType?: string | null
  scopeId?: string | null
  /** Backend-mapped scope for cmd-api; preserved when `scopeType` is cleared for search-api. */
  listMeetingsScopeType?: string | null
  userIds?: string[]
  ownerType?: string | null
} | null | undefined

export const resolveBackendScopeValue = (
  scopeType: string | null | undefined,
  scopeId: string | null | undefined,
): string | null => {
  const mapped = mapScopeTypeToBackendValue(scopeType, scopeId)
  if (mapped) return mapped
  if (scopeType && scopeType in LIST_MEETINGS_SCOPE_KIND_BY_BACKEND_VALUE) {
    return scopeType
  }
  return null
}

/**
 * cmd `ListMeetingsScopeInput` — always populated so cmd-api can authorize and filter by scope kind.
 * Optional `userUuids` narrows org-wide scopes when the filter modal selects specific users.
 */
export const buildListMeetingsScope = (
  uiFilters: ListMeetingsScopeUiFilters,
): ListMeetingsScopeInput => {
  if (!uiFilters) {
    return { kind: 'MY_CALENDLY' }
  }

  const { scopeType, scopeId, listMeetingsScopeType, userIds, ownerType } = uiFilters
  const scopeTypeForCmd = listMeetingsScopeType ?? scopeType
  let backendScope = resolveBackendScopeValue(scopeTypeForCmd, scopeId)
  if (!backendScope && ownerType === 'TEAM') backendScope = 'page'
  if (!backendScope && ownerType === 'USER') backendScope = 'all_users'
  let kind = (backendScope
    && LIST_MEETINGS_SCOPE_KIND_BY_BACKEND_VALUE[
      backendScope as keyof typeof LIST_MEETINGS_SCOPE_KIND_BY_BACKEND_VALUE
    ])
    || 'MY_CALENDLY'

  // Id-bearing kinds without a valid id fall back to caller scope (MY_CALENDLY).
  if (LIST_MEETINGS_SCOPES_REQUIRING_ID.has(kind)) {
    if (scopeId == null || scopeId === '' || scopeId === 'me') {
      kind = 'MY_CALENDLY'
    }
  }

  const scope: ListMeetingsScopeInput = { kind }

  if (LIST_MEETINGS_SCOPES_REQUIRING_ID.has(kind)) {
    scope.id = String(scopeId)
  }

  if (Array.isArray(userIds) && userIds.length > 0) {
    scope.userUuids = userIds
  }

  return scope
}
